import { useEffect, useState } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";

import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { authenticatedFetch } from "services/api";
import { WHOLSTER_REST_BASE_URL } from "services/config";

const creditPresentation = {
  search: { color: "info", icon: "search", title: "Search credits" },
  insight: { color: "success", icon: "insights", title: "Insight credits" },
  enrichment: { color: "warning", icon: "person_search", title: "Enrichment credits" },
};

const formatNumber = (value) => new Intl.NumberFormat().format(value || 0);

const formatDate = (value) => {
  if (!value) return "Not available";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

function CreditDetails({ credit }) {
  const overageLimit = credit.overageUnlimited
    ? "Unlimited"
    : formatNumber(credit.overageLimit);

  return (
    <MDBox mt={1} width="100%">
      <MDBox display="flex" justifyContent="space-between" mb={0.75}>
        <MDTypography variant="button" color="text">
          Included used
        </MDTypography>
        <MDTypography variant="button" fontWeight="bold">
          {formatNumber(credit.includedUsed)}
        </MDTypography>
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" mb={0.75}>
        <MDTypography variant="button" color="text">
          Included remaining
        </MDTypography>
        <MDTypography variant="button" fontWeight="bold">
          {formatNumber(credit.includedRemaining)}
        </MDTypography>
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" mb={0.75}>
        <MDTypography variant="button" color="text">
          Period allocation
        </MDTypography>
        <MDTypography variant="button" fontWeight="bold">
          {formatNumber(credit.includedAllocation)}
        </MDTypography>
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" mb={0.75}>
        <MDTypography variant="button" color="text">
          Overage used
        </MDTypography>
        <MDTypography variant="button" fontWeight="bold">
          {formatNumber(credit.overageUsed)} / {overageLimit}
        </MDTypography>
      </MDBox>
      <MDTypography variant="caption" color="text">
        Current period ends {formatDate(credit.billingPeriod?.periodEnd)}
      </MDTypography>
    </MDBox>
  );
}

function CreditUsage() {
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState("");
  const sessionId = useSelector((state) => state.login.sessionId);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();

    async function getUsage() {
      try {
        setError("");
        const response = await authenticatedFetch(
          `${WHOLSTER_REST_BASE_URL}/admin/primeTracers/usage`,
          sessionId,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        if (response.status === 401 || response.status === 403) {
          navigate("/authentication/sign-in");
          return;
        }

        if (!response.ok) {
          throw new Error("Credit usage could not be loaded.");
        }

        const result = await response.json();
        setUsage(result);
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(requestError.message || "Credit usage could not be loaded.");
        }
      }
    }

    getUsage();

    return () => controller.abort();
  }, [navigate, sessionId]);

  const credits = usage?.data?.credits || [];
  const subscription = usage?.data?.subscription;
  const nextBillingCycle = credits
    .map((credit) => credit.billingPeriod?.periodEnd)
    .filter(Boolean)
    .sort()[0];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4">Credit usage</MDTypography>
          {subscription && (
            <MDTypography variant="button" color="text">
              {subscription.planTier} {subscription.planType} plan · {subscription.status} · billed{
                " "
              }
              {subscription.billingInterval}
            </MDTypography>
          )}
          {nextBillingCycle && (
            <MDTypography display="block" variant="h6" mt={1}>
              Next billing cycle starts {formatDate(nextBillingCycle)}
            </MDTypography>
          )}
          {usage?.meta?.asOf && (
            <MDTypography display="block" variant="caption" color="text">
              Usage current as of {formatDate(usage.meta.asOf)}
            </MDTypography>
          )}
        </MDBox>

        {!usage && !error && (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress color="info" />
          </MDBox>
        )}

        {error && (
          <MDAlert color="error">
            <MDTypography variant="body2" color="white">
              {error}
            </MDTypography>
          </MDAlert>
        )}

        {usage && credits.length === 0 && (
          <Card>
            <MDBox p={3}>
              <MDTypography variant="body2" color="text">
                No credit usage is available for this subscription.
              </MDTypography>
            </MDBox>
          </Card>
        )}

        <Grid container spacing={3}>
          {credits.map((credit) => {
            const presentation = creditPresentation[credit.creditType] || {
              color: "dark",
              icon: "data_usage",
              title: `${credit.creditType} credits`,
            };

            return (
              <Grid item xs={12} md={6} xl={4} key={credit.creditType}>
                <DefaultDoughnutChart
                  icon={{ color: presentation.color, component: presentation.icon }}
                  title={presentation.title}
                  description={<CreditDetails credit={credit} />}
                  height="14rem"
                  chart={{
                    labels: ["Used", "Remaining"],
                    datasets: {
                      label: presentation.title,
                      data: [credit.includedUsed || 0, credit.includedRemaining || 0],
                      backgroundColors: [presentation.color, "light"],
                    },
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default CreditUsage;